/*
  RTK Query, as in the portal (src/app/services/index.ts), with the portal's token handling:
  the Bearer token on every call, a refresh when the JWT is about to expire, and one retry after a
  401. Here the calls go to the app's own API, which talks to the portal for the parent.
*/
import { createApi, fetchBaseQuery, retry, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import storage, { STORAGE_KEYS } from "app/utils/storage";

const TOKEN_REFRESH_BUFFER_SECONDS = 10 * 60;
export const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || "";

export const sessionEvents = new EventTarget();

export function storeTokens(jwt: string, refreshToken?: string | null) {
  storage.setItem(STORAGE_KEYS.TOKEN, jwt);
  if (refreshToken) storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
}

export function clearSession() {
  storage.removeItem(STORAGE_KEYS.TOKEN);
  storage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  storage.removeItem(STORAGE_KEYS.PROFILE);
  sessionEvents.dispatchEvent(new Event("signed-out"));
}

const isExpiringSoon = (token: string, bufferSeconds: number) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" && payload.exp - Date.now() / 1000 < bufferSeconds;
  } catch { return false; }
};

const rawQuery = fetchBaseQuery({
  baseUrl: `${API_BASE}/api`,
  prepareHeaders: (headers) => {
    const token = storage.getItem(STORAGE_KEYS.TOKEN);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return headers;
  },
});

async function performRefresh(): Promise<boolean> {
  const refreshToken = storage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${API_BASE}/api/account/refresh`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ refreshToken }) });
    if (!res.ok) return false;
    const d = await res.json();
    if (!d.jwt) return false;
    storeTokens(d.jwt, d.refreshToken);
    return true;
  } catch { return false; }
}

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = retry(
  async (args, api, extraOptions) => {
    const url = typeof args === "string" ? args : args.url;
    const isAuth = url.includes("/account/login") || url.includes("/account/refresh");
    const token = storage.getItem(STORAGE_KEYS.TOKEN);
    if (!isAuth && token && isExpiringSoon(token, TOKEN_REFRESH_BUFFER_SECONDS)) await performRefresh();

    let result = await rawQuery(args, api, extraOptions);
    if (!isAuth && result.error?.status === 401) {
      if (await performRefresh()) result = await rawQuery(args, api, extraOptions);
      if (result.error?.status === 401) { clearSession(); return { error: { status: "CUSTOM_ERROR", error: "Session expired", data: result.error.data } as FetchBaseQueryError }; }
    }
    return result;
  },
  { retryCondition: (error: any, _args, { attempt }) => String(error?.status || "").startsWith("5") && attempt < 2 },
);

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Me", "Timetable", "Chat", "Devices", "Announcements"],
  endpoints: () => ({}),
});

export const errorMessage = (e: unknown, fallback = "Something went wrong. Please try again.") => {
  const err = e as { data?: { error?: string }; error?: string } | undefined;
  return err?.data?.error || err?.error || fallback;
};

export default api;
